import { API_BASE_URL } from '../lib/constants';
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useNotification } from '../contexts/NotificationContext';
import YearRangeDatePicker from './components/YearRangeDatePicker';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Grid,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Button,
  Alert,
  AlertTitle,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  Box,
  Radio,
  RadioGroup,
  FormLabel,
  Stack
} from '@mui/material';
import 'react-phone-input-2/lib/material.css';
import PhoneInput from 'react-phone-input-2';
import theme from '../theme/theme';
import { ThemeProvider } from '@mui/material/styles';


const attendantOptions = [
  'Friend', 'Aunt', 'Uncle', 'Wife', 'Husband', 'Son', 'Daughter', 'Mother',
  'Father', 'Brother', 'Sister', 'Grandmother', 'Grandfather', 'Cousin',
  'Nephew', 'Niece', 'Mother-in-law', 'Father-in-law', 'Sister-in-law',
  'Brother-in-law', 'Guardian', 'Caretaker', 'Neighbor', 'Colleague',
  'Partner', 'Others'
];

const allergyOptions = [
  'Peanuts', 'Tree nuts', 'Shellfish', 'Fish', 'Milk (Dairy)', 'Eggs',
  'Wheat (Gluten)', 'Soy', 'Pollen', 'Dust mites', 'Latex', 'Insect stings',
  'Penicillin', 'Other medication allergies'
];

const emailDomains = [
  '@gmail.com', '@yahoo.com', '@outlook.com', '@hotmail.com', '@icloud.com'
];



const EditPatient = ({ onBack }) => {
  const { showNotification } = useNotification();
  const { uhid_no } = useParams();

  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    email: '',
    mobile: '',
    date_of_birth: '',
    address: '',
    attendantName: '',
    attendantPhone: '',
    attendantRelation: '',
    insuranceAvailable: false,
    anyAllergies: false,
    sex: '',
    age: '',
    uhidNo: '',
    mentionAllergies: [],
    payment_method: '',
    online_payment_option: '',
    card_type: '',
  });

  const [errors, setErrors] = useState({});
  const allergyInputRef = useRef(null);

  // Fetch patient data on mount
  useEffect(() => {
    const fetchPatientData = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/get_patient/${uhid_no}`);
        const data = response.data;
        const dob = data.date_of_birth ? new Date(data.date_of_birth).toISOString().split('T')[0] : '';
        setFormData({
          name: String(data.name || ''),
          title: String(data.title || ''),
          email: String(data.email || ''),
          mobile: String(data.mobile || ''),
          date_of_birth: dob || '',
          address: String(data.address || ''),
          attendantName: String(data.attendant_name || ''),
          attendantPhone: String(data.attendant_phone || ''),
          attendantRelation: String(data.attendant_relation || ''),
          insuranceAvailable: data.insurance_available === 1,
          anyAllergies: data.any_allergies,
          sex: String(data.sex || ''),
          age: data.age || '',
          uhidNo: String(data.uhid_no || ''),
          payment_method: String(data.payment_method || ''),
          online_payment_option: String(data.online_payment_option || ''),
          card_type: String(data.card_type || ''),
          mentionAllergies: Array.isArray(data.mention_allergies) ? data.mention_allergies : typeof data.mention_allergies === 'string' && data.mention_allergies.length > 0 ? data.mention_allergies.split(',').map(s => s.trim()) : []
        });
      } catch (error) {
        console.error('Error fetching patient data:', error);
        showNotification('error', 'Failed to load patient data.');
      }
    };

    fetchPatientData();
  }, [uhid_no, showNotification]);

  const getSuggestions = (input) => {
    const inputStr = String(input || '');
    if (!inputStr || inputStr.includes('@')) return [];
    return emailDomains.map(domain => inputStr + domain);
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData(prev => {
      let newValue = value;
      if (type === 'checkbox') {
        newValue = checked;
        if (name === 'anyAllergies' && !checked) {
          return { ...prev, [name]: newValue, mentionAllergies: [] };
        }
      }
      if (name === 'mobile' || name === 'attendantPhone') {
        if (!/^[0-9+\s()-]*$/.test(value)) return prev;
      }
      let updatedForm = { ...prev, [name]: newValue };
      if (name === 'date_of_birth') {
        const birthDate = new Date(value);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        if (today.getMonth() < birthDate.getMonth() || (today.getMonth() === birthDate.getMonth() && today.getDate() < birthDate.getDate())) age--;
        updatedForm.age = age >= 0 ? age : 0;
      }
      return updatedForm;
    });
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    let error = "";
    const emailRegex = /^[^\s@]+@[^\s@]+\.(com|in)$/i;

    switch (name) {
      case 'name': if (!String(value).trim()) error = 'Name is required'; break;
      case 'title': if (!value) error = 'Title is required'; break;
      case 'email': if (!String(value).trim()) error = 'Email is required'; else if (!emailRegex.test(value)) error = 'Invalid email format'; break;
      case 'mobile': if (!value || value.length < 10) error = 'Mobile number must be 10 digits'; break;
      case 'attendantName': if (!String(value).trim()) error = 'Attendant name is required'; break;
      case 'attendantPhone': if (!value || value.length < 10) error = 'Attendant phone must be 10 digits'; break;
      case 'attendantRelation': if (!String(value).trim()) error = 'Attendant relation is required'; break;
      case 'sex': if (!value) error = 'Sex is required'; break;
      case 'payment_method': if (!value) error = 'Payment method is required'; break;
      case 'address': if (!String(value).trim()) error = 'Address is required'; break;
      case 'date_of_birth': if (!value) error = 'Date of birth is required'; break;
      default: break;
    }
    if (error) setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const validate = () => {
    const tempErrors = {};
    if (!formData.name.trim()) tempErrors.name = 'Name is required';
    if (!formData.email.trim()) tempErrors.email = 'Email is required';
    if (!formData.mobile || formData.mobile.length < 10) tempErrors.mobile = 'Mobile number must be 10 digits';
    if (!formData.age || formData.age <= 0) tempErrors.age = 'Valid age is required';
    if (!formData.sex) tempErrors.sex = 'Sex is required';

    setErrors(tempErrors);
    if (Object.keys(tempErrors).length > 0) {
      showNotification('error', `Please fix: ${Object.values(tempErrors)[0]}`);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const currentTypedAllergy = allergyInputRef.current?.value?.trim();
    let finalMentionAllergies = [...formData.mentionAllergies];
    if (formData.anyAllergies && currentTypedAllergy && !finalMentionAllergies.includes(currentTypedAllergy)) {
      finalMentionAllergies.push(currentTypedAllergy);
    }
    if (formData.anyAllergies && finalMentionAllergies.length === 0) {
      showNotification('error', "Please mention at least one allergy or uncheck 'Any Allergies'.");
      return;
    }
    if (formData.payment_method === "Online" && !formData.online_payment_option) {
      showNotification('error', "Please select an Online Payment Option.");
      return;
    }
    if (formData.payment_method === "Online" && formData.online_payment_option === "card" && !formData.card_type) {
      showNotification('error', "Please select Card Type.");
      return;
    }

    const formattedData = {
      title: formData.title,
      name: formData.name,
      email: formData.email,
      date_of_birth: formData.date_of_birth,
      mobile: formData.mobile,
      address: formData.address,
      attendant_name: formData.attendantName,
      attendant_phone: formData.attendantPhone,
      attendant_relation: formData.attendantRelation,
      insurance_available: formData.insuranceAvailable ? 1 : 0,
      any_allergies: formData.anyAllergies ? 1 : 0,
      mention_allergies: finalMentionAllergies.join(', '),
      uhid_no: formData.uhidNo,
      age: formData.age,
      sex: formData.sex,
      payment_method: formData.payment_method || 'Cash',
      online_payment_option: formData.online_payment_option || 'upi',
      card_type: formData.card_type || null,
    };

    try {
      await axios.put(`${API_BASE_URL}/update_patient/${uhid_no}`, formattedData, { withCredentials: true });
      showNotification('success', 'Patient details updated successfully!');
      navigate('/home', { state: { activeMenu: 'Patient' } })
    } catch (error) {
      console.error('Update error:', error);
      const msg = error.response?.data?.error || 'Error updating patient details.';
      showNotification('error', msg);
    }
  };


  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 3 }}>
          <button
            onClick={() => navigate('/home', { state: { activeMenu: 'Patient' } })}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: '#008080',
              fontSize: '16px',
              fontWeight: 500,
              cursor: 'pointer',
              marginRight: '16px',
            }}
          >
            ← Back
          </button>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 2 }}>
            {/* Heading with Icon */}
            <Typography
              variant="h4"
              sx={{
                color: '#008080',
                display: 'flex',
                alignItems: 'center',
                fontWeight: 600,
                letterSpacing: 0.5,
              }}
            >

              PATIENT REGISTRATION FORM
            </Typography>

            {/* Right-aligned Logo and Text */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img src="/logo.png" alt="Logo" className="w-8 h-8" />
              <Typography
                variant="h6"
                sx={{
                  color: '#008080',
                  marginLeft: 1,
                  fontWeight: 500,
                  letterSpacing: 0.3,
                }}
              >
                OTMS
              </Typography>
            </Box>
          </Box>


          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {/* Name */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name}
                  onBlur={handleBlur}
                />
              </Grid>

              {/* Title */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required error={!!errors.title}>
                  <InputLabel>Title</InputLabel>
                  <Select
                    name="title"
                    required
                    value={formData.title}
                    onChange={handleChange}
                    label="Title"
                    onBlur={handleBlur}
                  >
                    <MenuItem value="Mr.">Mr</MenuItem>
                    <MenuItem value="Mrs.">Mrs</MenuItem>
                    <MenuItem value="Ms.">Ms</MenuItem>
                    <MenuItem value="Dr.">Dr</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Email with autocomplete */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  freeSolo
                  options={getSuggestions(formData.email)}
                  inputValue={formData.email}
                  onInputChange={(e, newInputValue) => {
                    setFormData(prev => ({ ...prev, email: newInputValue }));
                  }}
                  onChange={(e, newValue) => {
                    setFormData(prev => ({ ...prev, email: newValue || '' }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Email"
                      name="email"
                      type="email"
                      required
                      fullWidth
                      error={!!errors.email}
                      helperText={errors.email || ' '}
                      onBlur={handleBlur}
                    />
                  )}
                />
              </Grid>

              {/* Mobile */}
              <Grid item xs={12} sm={6}>
                <PhoneInput
                  country={'in'}
                  value={formData.mobile}
                  required
                  onChange={(value) =>
                    setFormData((prev) => ({ ...prev, mobile: value }))
                  }
                  enableSearch
                  inputProps={{
                    name: 'mobile',
                    required: true,
                    autoFocus: true,
                  }}

                  containerStyle={{ width: '100%' }}
                  inputStyle={{ width: '100%', height: '56px' }}
                  buttonStyle={{ height: '56px' }}
                  onBlur={() => handleBlur({ target: { name: 'mobile', value: formData.mobile } })}
                />
                <div
                  style={{
                    color: errors.mobile ? '#d32f2f' : 'rgba(0, 0, 0, 0.6)',
                    fontSize: '0.75rem',
                    marginTop: '4px',
                    marginLeft: '14px',
                  }}
                >
                  {errors.mobile || ' '}
                </div>

              </Grid>
              {/* <Grid item xs={12} sm={6}><TextField label="Data of Brith" value={formData.date_of_birth}name="date_of_birth"
                onChange={handleChange}
                required
                type='date'
                 InputLabelProps={{ shrink: true }}
    inputProps={{
	     max: new Date().toISOString().split("T")[0], // Prevents future dates
    }}
                fullWidth/></Grid> */}
              <Grid item xs={12} sm={6}>
                <YearRangeDatePicker
                  label="Date of Birth"
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleChange}
                  required
                  onBlur={handleBlur}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Age"
                  name="age"
                  type="number"
                  value={formData.age}
                  onChange={handleChange}
                  required
                  InputProps={{ readOnly: true }}
                  fullWidth
                  error={!!errors.age}
                  helperText={errors.age || ' '}
                />
              </Grid>

              {/* Address */}
              <Grid item xs={12}>
                <TextField
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  fullWidth
                  error={!!errors.address}
                  helperText={errors.address}
                  onBlur={handleBlur}
                />
              </Grid>

              {/* Attendant Name */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Attendant Name"
                  name="attendantName"
                  value={formData.attendantName}
                  onChange={handleChange}
                  required
                  fullWidth
                  error={!!errors.attendantName}
                  helperText={errors.attendantName}
                  onBlur={handleBlur}
                />
              </Grid>

              {/* Attendant Phone */}
              <Grid item xs={12} sm={6}>
                <PhoneInput
                  country={'in'}
                  value={formData.attendantPhone}
                  onChange={(value) =>
                    setFormData((prev) => ({ ...prev, attendantPhone: value }))
                  }
                  required
                  enableSearch
                  inputProps={{
                    name: 'attendant_phone',
                    required: true,
                    autoFocus: true,
                  }}
                  containerStyle={{ width: '100%' }}
                  inputStyle={{
                    width: '100%',
                    height: '56px',
                    borderColor: errors.attendant_phone ? '#d32f2f' : undefined,
                  }}
                  buttonStyle={{ height: '56px' }}
                  onBlur={() => handleBlur({ target: { name: 'attendantPhone', value: formData.attendantPhone } })}
                />
                {/* Helper/error text styled like MUI */}
                <div
                  style={{
                    color: errors.attendantPhone ? '#d32f2f' : 'rgba(0, 0, 0, 0.6)',
                    fontSize: '0.75rem',
                    marginTop: '4px',
                    marginLeft: '14px',
                  }}
                >
                  {errors.attendantPhone || ' '}
                </div>
              </Grid>
              {/* Attendant Relation */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  freeSolo
                  options={attendantOptions}
                  value={formData.attendantRelation}
                  onChange={(e, newValue) => {
                    setFormData(prev => ({ ...prev, attendantRelation: newValue || '' }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Attendant Relation"
                      required
                      fullWidth
                      error={!!errors.attendantRelation}
                      helperText={errors.attendantRelation}
                      onBlur={handleBlur}
                    />
                  )}
                />
              </Grid>

              {/* Sex */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required error={!!errors.sex}>
                  <InputLabel>Sex</InputLabel>
                  <Select
                    name="sex"
                    value={formData.sex}
                    onChange={handleChange}
                    label="Sex"
                    onBlur={handleBlur}
                  >
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                  {errors.sex && (
                    <Typography color="error" variant="caption">
                      {errors.sex}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    label="Payment Method"
                    name="payment_method"
                    value={formData.payment_method}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <MenuItem value="Cash">Cash</MenuItem>
                    <MenuItem value="Insurance">Insurance</MenuItem>
                    <MenuItem value="Online">Online</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {formData.payment_method === "Online" && (
                <Box display="flex" flexDirection="column" sx={{ mt: '14px', ml: 2 }}>
                  <FormLabel sx={{ fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)', mb: 0 }}>
                    Select Online Payment Method
                  </FormLabel>
                  <RadioGroup
                    row
                    value={formData.online_payment_option}
                    onChange={(e) =>
                      setFormData({ ...formData, online_payment_option: e.target.value, card_type: "" })
                    }
                  >
                    <FormControlLabel value="upi" control={<Radio />} label="UPI" />
                    <FormControlLabel value="card" control={<Radio />} label="Card" />
                    <FormControlLabel value="netbanking" control={<Radio />} label="Net Banking" />
                  </RadioGroup>

                  {/* Show Card Type only if Card is selected */}
                  {formData.online_payment_option === "card" && (
                    <FormControl sx={{ mt: 2 }}>
                      <FormLabel>Card Type</FormLabel>
                      <RadioGroup
                        row
                        value={formData.card_type}
                        onChange={(e) => setFormData({ ...formData, card_type: e.target.value })}
                      >
                        <FormControlLabel value="Debit" control={<Radio />} label="Debit Card" />
                        <FormControlLabel value="Credit" control={<Radio />} label="Credit Card" />
                      </RadioGroup>
                    </FormControl>
                  )}
                </Box>
              )}
              {/* Any Allergies Checkbox */}
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.anyAllergies}
                      onChange={handleChange}
                      name="anyAllergies"
                    />
                  }
                  label="Any Allergies"
                />
              </Grid>



              {/* Age */}
              {formData.anyAllergies && (
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={allergyOptions}
                    value={formData.mentionAllergies}
                    onChange={(event, newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        mentionAllergies: Array.isArray(newValue) ? newValue : [],
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Mention Allergies"
                        inputRef={allergyInputRef}  // Attach input ref
                        onBlur={() => {
                          const typedValue = allergyInputRef.current?.value?.trim();
                          if (
                            typedValue &&
                            !formData.mentionAllergies.includes(typedValue)
                          ) {
                            setFormData((prev) => ({
                              ...prev,
                              mentionAllergies: [...prev.mentionAllergies, typedValue],
                            }));
                          }
                        }}
                        fullWidth
                      />
                    )}
                  />

                </Grid>
              )}
              {/* UHID No (read-only) */}
              <Grid item xs={12} >
                <TextField
                  label="UHID No"
                  name="uhidNo"
                  type="text"
                  value={formData.uhidNo}
                  InputProps={{ readOnly: true }}

                  fullWidth
                />
              </Grid>


            </Grid>

            <Button type="submit" variant="contained" color="primary" sx={{ mt: 3 }} fullWidth>
              Update Patient Registration
            </Button>
          </form>
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default EditPatient;
